import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import dayjs from 'dayjs';
import DatePicker from 'react-multi-date-picker';
import { toast } from 'react-toastify';
import AllocateRooms from './AllocateRooms';
import RoomBilling from '../Invoice/RoomBilling';
import { API_GET_BOOKED_ROOMS, API_GET_INVOICE } from '../../../Util/URLConstants';
import Loader from '../../../Components/Loader';
import { ROUTE_ENQUIRY_DETAILS, SESSION_USER_ID_KEY } from '../../../Util/VMConstants';
import StorageFactory from '../../../Util/StorageFactory';
import { returnDate } from '../../../Util/UtilHelper';
import { VM_Colors } from '../../../Styles/VMStyles';

function RoomAllocateAndPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const bookedSectionRef = useRef(null);

  const customer = location?.state?.customer ?? null;
  const events = location?.state?.events ?? [];

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [showBookedRooms, setShowBookedRooms] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [searchTabs, setSearchTabs] = useState([]);
  const [hasInvoice, setHasInvoice] = useState(false);

  const sessionFactory = useMemo(() => new StorageFactory('session'), []);
  const userID = sessionFactory.get(SESSION_USER_ID_KEY);

  const applyMargin = location?.pathname?.includes('roomsAllocation');

  const eventDatesLabel = useMemo(() => {
    if (!customer?.appointment_date) return '—';
    const dates = Array.isArray(customer.appointment_date)
      ? customer.appointment_date
      : [customer.appointment_date];
    return dates.map(returnDate).join(', ');
  }, [customer]);

  const getBookedRoomsInfo = useCallback(async (signal) => {
    if (!userID || !customer?.appointment_id) return;

    setErrorMsg('');
    setLoading(true);
    try {
      const url = `${API_GET_BOOKED_ROOMS}?user_id=${userID}&enquiry_id=${customer.appointment_id}`;
      const config = signal ? { signal } : {};
      const resp = await axios.get(url, config);
      const respData = resp?.data?.data;
      setRooms(respData?.rooms_info ?? []);
    } catch (err) {
      if (err?.name === 'CanceledError' || axios.isCancel?.(err)) {
        return;
      }
      console.error('Error fetching booked rooms:', err);
      setErrorMsg('Failed to load room bookings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userID, customer?.appointment_id]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!customer?.appointment_id) return;
    const controller = new AbortController();

    const checkInvoiceAndLoadRooms = async () => {
      try {
        const resp = await axios.get(API_GET_INVOICE, {
          params: { enquiry_id: customer.appointment_id },
          signal: controller.signal,
        });
        const invoiceDetails = resp?.data?.invoice_info || [];
        const exists = Array.isArray(invoiceDetails) && invoiceDetails.length > 0;
        setHasInvoice(exists);
        if (exists) {
          setShowBookedRooms(true);
          await getBookedRoomsInfo(controller.signal);
        }
      } catch (err) {
        if (err?.name === 'CanceledError' || axios.isCancel?.(err)) return;
        console.error('Error checking invoice status:', err);
      }
    };

    checkInvoiceAndLoadRooms();
    return () => {
      try { controller.abort(); } catch (e) {}
    };
  }, [customer?.appointment_id, getBookedRoomsInfo]);

  const handleSearch = () => {
    if (!selectedDates.length) {
      toast.error('Please select at least one date');
      return;
    }
    const formattedDates = selectedDates.map((d) => dayjs(d).format('DD-MM-YYYY'));
    setSearchTabs(formattedDates);
  };

  const handleAllocationSuccess = async () => {
    setShowBookedRooms(true);
    const controller = new AbortController();
    await getBookedRoomsInfo(controller.signal);
    setTimeout(() => {
      bookedSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleBack = () => {
    navigate('/' + ROUTE_ENQUIRY_DETAILS, {
      replace: true,
      state: {
        enquiry_id: customer?.appointment_id,
        navigate_from: 'roomsallocation',
      },
    });
  };

  const handleShowAllocateView = () => {
    if (hasInvoice) return;
    setShowBookedRooms(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!customer) {
    return (
      <Paper sx={{ padding: 2, ...(applyMargin ? { ml: { md: 5 } } : {}) }}>
        <Typography variant="h6" color="error">
          Error: Customer information is missing.
        </Typography>
        <Button onClick={() => navigate('/' + ROUTE_ENQUIRY_DETAILS, { replace: true })} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 2, ...(applyMargin ? { ml: { md: 2 } } : {}) }}>
      {/* Section 1 — allocate */}
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1}>
          <Button variant="outlined" size="small" onClick={handleBack} aria-label="Back">
            ← Back
          </Button>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>
            Rooms Allocation
          </Typography>
        </Box>
        {/* <Button variant="outlined" onClick={scrollToBookedRooms}>
          Continue
        </Button> */}
      </Box>

      {/* Preview row */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: VM_Colors.primary,
          borderRadius: 2,
        }}
      >
        <Grid container spacing={10}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" color={VM_Colors.white}>
              Enquiry Person Name
            </Typography>
            <Typography variant="h3" fontWeight={600} color={VM_Colors.white}>
              {customer.name || '—'}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2"  color={VM_Colors.white}>
              Event Dates
            </Typography>
            <Typography variant="h3" fontWeight={600} color={VM_Colors.white}>
              {eventDatesLabel}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {!showBookedRooms && !hasInvoice && (
        <>
          {/* Select dates + Search */}
          <Paper
            elevation={1}
            sx={{
              p: 2,
              mb: 2,
              backgroundColor: VM_Colors.white,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: '100%',
                maxWidth: 560,
                display: 'flex',
                flexDirection: isSmallScreen ? 'column' : 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Box>
                <Typography
                  color="text.secondary"
                  sx={{ mb: 0.5, textAlign: 'left', width: '100%', fontWeight: 700, fontSize: '14px' }}
                >
                  Select dates
                </Typography>
                <DatePicker
                  multiple
                  value={selectedDates}
                  onChange={setSelectedDates}
                  format="DD-MM-YYYY"
                  placeholder="DD-MM-YYYY"
                  minDate={new Date()}
                  style={{
                    width: '100%',
                    maxWidth: 320,
                    minWidth: 250,
                    height: 40,
                    backgroundColor: '#f5f5f5',
                    fontSize: '16px',
                    padding: '4px 12px',
                    borderRadius: 6,
                  }}
                />
                </Box>
              </Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                sx={{
                  minWidth: 150,
                  height: 40,
                  fontWeight: 600,
                  alignSelf: isSmallScreen ? 'center' : 'flex-end',
                  mt: isSmallScreen ? 0 : 2.5,
                }}
              >
                Search
              </Button>
            </Box>
          </Paper>

          <Box sx={{ backgroundColor: VM_Colors.white2, borderRadius: 2, mb: 3 }}>
            <AllocateRooms
              customer={customer}
              hideDateSearch
              externalTabs={searchTabs}
              onAllocationSuccess={handleAllocationSuccess}
              allocateButtonLabel="Allocate and Continue"
              showBottomAllocateButton
            />
          </Box>
        </>
      )}

      {showBookedRooms && (
        <Box ref={bookedSectionRef}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
            mb={2}
          >
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Booked Rooms Data
            </Typography>
            <Tooltip
              title={hasInvoice ? "Rooms cannot be allocated after an invoice is created" : ""}
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={hasInvoice}
                  onClick={handleShowAllocateView}
                >
                  Allocate Rooms
                </Button>
              </span>
            </Tooltip>
          </Box>
            {rooms.length > 0 ? (
              <RoomBilling
                bookedRoomsState={rooms}
                bookedEventState={events}
                displayHeading={false}
                readOnly={hasInvoice}
              />
            ) : (
              <Paper
                sx={{
                  p: 3,
                  textAlign: 'center',
                  backgroundColor: VM_Colors.white2,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {errorMsg || 'No room bookings found.'}
                </Typography>
              </Paper>
            )}
        </Box>
      )}

      <Loader loading={loading} />
    </Paper>
  );
}

export default RoomAllocateAndPreview;
